/* Wemond brew guides page (sections/wemond-brew-guides.liquid).
   Equipment filter + cards that turn over to show the coffee's own recipe.
   Recipe data: window.WemondProductRecipes / WemondRecipeUtils
   (assets/wemond-product-recipes.js). */
(() => {
  const root = document.querySelector('[data-wbg]');
  if (!root || root.dataset.wbgReady) return;
  root.dataset.wbgReady = 'true';

  const METHODS = [
    ['espresso', 'Espresso'],
    ['mokapot', 'Moka Pot'],
    ['v60', 'V60'],
    ['origami', 'Origami'],
    ['chemex', 'Chemex'],
    ['aeropress', 'AeroPress'],
    ['filtre-makinesi', 'Filtre Kahve Makinesi'],
    ['frenchpress', 'French Press'],
    ['coldbrew', 'Cold Brew'],
    ['japanese', 'Japanese Iced'],
  ];
  const LABEL = Object.fromEntries(METHODS);
  const FLIP_MS = 700;

  const grid = root.querySelector('[data-wbg-grid]');
  const status = root.querySelector('[data-wbg-status]');
  const empty = root.querySelector('[data-wbg-empty]');
  const pager = root.querySelector('[data-wbg-pager]');
  const methodButtons = [...root.querySelectorAll('[data-wbg-methods] [data-wbg-method]')];
  let cards = [];
  let current = 'all';

  const esc = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function whenRecipesReady(callback) {
    if (window.WemondProductRecipes && window.WemondRecipeUtils) return callback();
    let done = false;
    const run = () => {
      if (done || !(window.WemondProductRecipes && window.WemondRecipeUtils)) return;
      done = true;
      callback();
    };
    document.addEventListener('wemond:product-recipes-ready', run, { once: true });
    window.addEventListener('load', run, { once: true });
  }

  function normalize(key) {
    if (!key) return 'all';
    const k = window.WemondRecipeUtils.normalizeKey(String(key).toLowerCase());
    return LABEL[k] ? k : 'all';
  }

  function recipeFor(card, key) {
    return window.WemondRecipeUtils.find(card.dataset.handle, key);
  }

  // Same lookup as the homepage brew guide (honduras-shb shares honduras-shg).
  function productProfile(card) {
    const recipes = window.WemondProductRecipes;
    return recipes[card.dataset.handle]?.profile || (card.dataset.handle === 'honduras-shb' ? recipes['honduras-shg']?.profile : '') || '';
  }

  function renderBack(card, key) {
    const recipe = recipeFor(card, key);
    const back = card.querySelector('[data-wbg-back]');
    if (!recipe || !back) return;
    const utils = window.WemondRecipeUtils;
    const grind = [utils.grinderLabel(recipe.key), utils.grinderRefs(recipe.grind, recipe.key)].filter(Boolean).join(' · ');
    const metrics = [
      ['Oran', recipe.ratio],
      ['Öğütüm', grind],
      ['Su sıcaklığı', recipe.temperature],
      ['Süre', recipe.duration],
    ].filter((m) => m[1]);
    const methods = card._methods;
    const profile = productProfile(card);
    const adjust = Array.isArray(recipe.adjust) ? recipe.adjust : [];

    back.innerHTML = `
      <div class="wbg-back__head">
        <div>
          <p class="wbg-back__product">${esc(card.dataset.title)}</p>
          <h3 class="wbg-back__title" data-wbg-back-title tabindex="-1"><em>${esc(recipe.label || LABEL[key])} reçetesi</em></h3>
        </div>
        <button type="button" class="wbg-back__close" data-wbg-close aria-label="Karta dön"><span aria-hidden="true">×</span></button>
      </div>
      ${methods.length > 1 ? `<div class="wbg-back__methods" role="group" aria-label="Bu kahve için ekipman">${methods
        .map((m) => `<button type="button" data-wbg-recipe="${m}" aria-pressed="${m === key}">${esc(LABEL[m])}</button>`)
        .join('')}</div>` : ''}
      ${profile ? `<p class="wbg-back__lead">${esc(profile)}</p>` : ''}
      <div class="wbg-back__metrics">${metrics.map((m) => `<span><small>${esc(m[0])}</small><b>${esc(m[1])}</b></span>`).join('')}</div>
      <div class="wbg-back__steps"><p class="wbg-back__label">Adım adım</p><ol>${recipe.steps_html || ''}</ol></div>
      ${recipe.note ? `<div class="wbg-back__note"><p class="wbg-back__label">Fincanda</p><p>${esc(recipe.note)}</p></div>` : ''}
      ${adjust.length ? `<div class="wbg-back__adjust"><p class="wbg-back__label">İnce ayar</p><dl>${adjust
        .map((a) => `<div><dt>${esc(a[0])}</dt><dd>${esc(a[1])}</dd></div>`)
        .join('')}</dl></div>` : ''}
      <div class="wbg-back__actions">
        <button type="button" class="wbg-btn" data-wbg-close>Karta dön</button>
        <a class="wbg-btn wbg-btn--solid" href="${esc(card.dataset.url)}">Ürün sayfasına git</a>
      </div>`;
    back.scrollTop = 0;
    card._recipeKey = key;
  }

  function setFaces(card, flipped) {
    const front = card.querySelector('[data-wbg-front]');
    const back = card.querySelector('[data-wbg-back]');
    front.inert = flipped;
    back.inert = !flipped;
    card.querySelector('[data-wbg-open]')?.setAttribute('aria-expanded', String(flipped));
  }

  function flip(card, key) {
    const methodKey = key && card._methods.includes(key) ? key : card._methods[0];
    renderBack(card, methodKey);
    card.classList.add('is-flipped');
    setFaces(card, true);
    const title = card.querySelector('[data-wbg-back-title]');
    window.setTimeout(() => title?.focus({ preventScroll: true }), reducedMotion() ? 0 : FLIP_MS / 2);
  }

  function unflip(card, restoreFocus = true) {
    if (!card.classList.contains('is-flipped')) return;
    card.classList.remove('is-flipped');
    setFaces(card, false);
    if (restoreFocus) card.querySelector('[data-wbg-open]')?.focus({ preventScroll: true });
  }

  function visibleCards() {
    return cards.filter((card) => !card.hidden);
  }

  function updatePager() {
    if (!pager) return;
    const list = visibleCards();
    if (!list.length || grid.scrollWidth <= grid.clientWidth + 2) {
      pager.textContent = '';
      return;
    }
    const step = list.length > 1 ? list[1].offsetLeft - list[0].offsetLeft : grid.clientWidth;
    const index = Math.min(list.length, Math.round(grid.scrollLeft / Math.max(1, step)) + 1);
    pager.textContent = `${index} / ${list.length}`;
  }

  function select(key, { updateUrl = true } = {}) {
    current = key;
    methodButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.wbgMethod === key)));
    cards.forEach((card) => unflip(card, false));

    let shown = 0;
    cards.forEach((card) => {
      const visible = key === 'all' || card._methods.includes(key);
      card.hidden = !visible;
      if (visible) shown += 1;
    });

    empty.hidden = shown > 0;
    status.textContent = key === 'all' ? `${shown} kahve · tüm ekipmanlar` : `${LABEL[key]} ile demlenebilen ${shown} kahve`;

    if (!reducedMotion()) {
      grid.classList.remove('is-filtering');
      void grid.offsetWidth;
      grid.classList.add('is-filtering');
    }
    grid.scrollLeft = 0;
    updatePager();

    if (updateUrl) {
      const url = new URL(window.location.href);
      if (key === 'all') url.searchParams.delete('yontem');
      else url.searchParams.set('yontem', key);
      window.history.replaceState(window.history.state, '', url);
    }
  }

  function init() {
    cards = [...root.querySelectorAll('[data-wbg-card]')];
    const counts = Object.fromEntries(METHODS.map(([k]) => [k, 0]));

    cards = cards.filter((card) => {
      // Recipe-file order (primary brewer first), same as the product page.
      const entry = window.WemondProductRecipes[card.dataset.handle] || (card.dataset.handle === 'honduras-shb' ? window.WemondProductRecipes['honduras-shg'] : null);
      const ordered = (entry?.recipes || []).map((r) => window.WemondRecipeUtils.normalizeKey(r.key));
      const methods = [...new Set([...ordered, ...METHODS.map(([k]) => k)])].filter((k) => LABEL[k] && recipeFor(card, k));
      if (!methods.length) {
        card.remove();
        return false;
      }
      card._methods = methods;
      methods.forEach((k) => (counts[k] += 1));
      const line = card.querySelector('[data-wbg-card-methods]');
      if (line) line.textContent = methods.map((k) => LABEL[k]).join(' · ');
      setFaces(card, false);
      return true;
    });

    methodButtons.forEach((button) => {
      const key = button.dataset.wbgMethod;
      const count = key === 'all' ? cards.length : counts[key] || 0;
      const badge = button.querySelector('[data-wbg-count]');
      if (badge) badge.textContent = count;
      if (key !== 'all' && count === 0) button.hidden = true;
    });

    root.classList.add('is-ready');
    const requested = normalize(new URL(window.location.href).searchParams.get('yontem'));
    select(requested, { updateUrl: false });
    if (requested !== 'all') {
      root.querySelector(`[data-wbg-methods] [data-wbg-method="${requested}"]`)?.scrollIntoView({ block: 'nearest', inline: 'center' });
    }
  }

  root.addEventListener('click', (event) => {
    const method = event.target.closest('[data-wbg-method]');
    if (method && root.contains(method)) {
      select(method.dataset.wbgMethod);
      return;
    }
    const card = event.target.closest('[data-wbg-card]');
    if (!card) return;
    if (event.target.closest('[data-wbg-open]')) {
      flip(card, current === 'all' ? null : current);
    } else if (event.target.closest('[data-wbg-close]')) {
      unflip(card);
    } else {
      const recipeButton = event.target.closest('[data-wbg-recipe]');
      if (recipeButton) {
        renderBack(card, recipeButton.dataset.wbgRecipe);
        card.querySelector(`[data-wbg-recipe="${recipeButton.dataset.wbgRecipe}"]`)?.focus({ preventScroll: true });
      }
    }
  });

  root.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const card = event.target.closest?.('[data-wbg-card].is-flipped');
    if (card) unflip(card);
  });

  let pagerFrame = 0;
  grid.addEventListener('scroll', () => {
    cancelAnimationFrame(pagerFrame);
    pagerFrame = requestAnimationFrame(updatePager);
  }, { passive: true });
  window.addEventListener('resize', updatePager, { passive: true });

  whenRecipesReady(init);
})();
